import { Injectable, Injector, LOCALE_ID, inject } from "@angular/core";
import { Utilities, EmailShare, EmailOptions } from "@tylertech/shared-module";
import
{
    ApiService, FileService, ErrorInfo, CacheHelperService, ToastService, StorageService,
    CachedRepositoryService,
    INotificationActionInterface
} from "@tylertech/core-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { IntentService } from "@tylertech/intents-module";
import
{
    IMetadataAction,
    MetadataVerification,
    MetadataCollectionVerification,
    RepositoryDataCollection,
    IMetadataResequenceAction,
    PropertyMetadata,
    AfterSaveContext,
    IDocument,
    KeyValuePair
} from "@tylertech/tyler-api-types";
import { AugmentedModelObject, IClientSideMetadataAction, RepositoryFormData } from "../interfaces";
import { MetadataActionHelpers, ObjectHelpers } from "../utilities";
import { MetadataService } from "./metadata.service";
import { MetadataIframeService } from "./metadataIframeService";
import { MetadataViewService } from "./metadataViewService";
import { MetadataErrorService } from "./metadataErrorService";
import { MetadataPropertyService } from "./metadataPropertyService";
import { MetadataEventService } from "./metadataEventService";
import { IShellService } from "@tylertech/incode-module";
import { formatCurrency, formatDate } from "@angular/common";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MetadataActionDialogComponent, MetadataActionDialogData } from "../components/metadataActionDialog/metadataActionDialog";
import { HttpErrorResponse } from "@angular/common/http";
import { GridEventService } from "./table/gridEvent.service";
import { MultipleEntityEditService } from "./multipleEntityEditService";

@Injectable({ providedIn: "root" })
export class MetadataActionService implements INotificationActionInterface
{
    private readonly matDialog = inject(MatDialog);
    private $storageService = inject(StorageService);
    private readonly $apiService = inject(ApiService);
    private $fileService = inject(FileService);
    private $repositoryService = inject(CachedRepositoryService);
    private readonly $cacheHelperService = inject(CacheHelperService);
    private $intentService = inject(IntentService);
    private readonly $metadataIframeService = inject(MetadataIframeService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $gridEventService = inject(GridEventService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $multipleEntityEditService = inject(MultipleEntityEditService);
    private injector = inject(Injector);
    private readonly $toastService = inject(ToastService);
    private readonly $shellService = inject(IShellService);
    locale = inject(LOCALE_ID);

    isOverrideSaveAction: boolean = false;
    delayTabClose: boolean = false;
    actionRedirecting: boolean = false;
    activeDialogIndex = 0;
    closeDialogAction: boolean = false;

    public static WindowSave: string = "window_save";
    public static DocViewerLoaded: string = "doc_viewer_loaded";

    HasSubitems(action: IMetadataAction): boolean
    {
        return !!action.SubmenuActions;
    }

    GetSubitems(action: IMetadataAction): IMetadataAction[]
    {
        if (this.HasSubitems(action))
        {
            return action.SubmenuActions;
        }
    }

    GetNewRepositoryFormData(action: IMetadataAction, model?: any): RepositoryFormData
    {
        if (Utilities.IsNullOrUndefined(action.ViewToShow)) return null;
        let typeName: string;
        if (!Utilities.IsNullOrUndefined(model) && model["$$TypeName"]) typeName = model["$$TypeName"];
        else typeName = this.$metadataViewService.GetTypeName(action.ViewToShow);
        return new RepositoryFormData(typeName, null);
    }

    MailToIfNeeded(model: any, action: IMetadataAction): boolean
    {
        if (action && action.MailOptions)
        {
            EmailShare.send(<EmailOptions>{
                recipient: action.MailOptions.Recipient,
                subject: action.MailOptions.Subject,
                message: action.MailOptions.Message
            });
            return true;
        }
        return false;
    }

    RedirectIfNeeded(model: any, action: IMetadataAction): boolean
    {
        if (action && action.RedirectToForm)
        {
            this.actionRedirecting = true;
            const app = action.RedirectToForm.App ?? "form";
            let url = "/" + window.location.pathname.split("/")[1].replace(/[^a-zA-Z0-9_-]/g, "") + `/apps/${app}`;

            if (action.RedirectToForm.Path)
                url += `/?path=${encodeURIComponent(action.RedirectToForm.Path)}`;
            if (action.RedirectToForm.Filter)
            {
                const parent = model ? model["$$Parent"] : null;
                const combiner = action.RedirectToForm.Path ? "&" : "";
                url += `${combiner}${this.$metadataService.EvaluateString(action.RedirectToForm.Filter, parent, model, null, true)}`;
            }
            if (action.RedirectToForm.RedirectUrl)
                url = action.RedirectToForm.RedirectUrl;
            if (!Utilities.IsNullOrUndefined(action.FormViewToShow))
            {
                url += url.includes("?") ? "&view=" : "?view=";
                url += action.FormViewToShow;
            }

            window.location.href = url;
            return true;
        }
        return false;
    }

    OpenNewTabIfNeeded(model: any, action: IMetadataAction, afterCloseTab?: () => void, afterSaveContext?: AfterSaveContext): boolean
    {
        if (action && action.FormToOpenInNewTab)
        {
            const app = action.FormToOpenInNewTab.App ?? "form";
            let url = "/" + window.location.pathname.split("/")[1].replace(/[^a-zA-Z0-9_-]/g, "") + `/apps/${app}`;

            if (action.FormToOpenInNewTab.Path)
                url += `/?path=${encodeURIComponent(action.FormToOpenInNewTab.Path)}`;
            if (action.FormToOpenInNewTab.Filter)
            {
                const parent = model ? model["$$Parent"] : null;
                const combiner = action.FormToOpenInNewTab.Path ? "&" : "";
                url += `${combiner}${this.$metadataService.EvaluateString(action.FormToOpenInNewTab.Filter, parent, model, null, true)}`;
            }
            url += url.includes("?") ? "&n=true" : "?n=true";
            if (action.FormToOpenInNewTab.RedirectUrl)
            {
                url = action.FormToOpenInNewTab.RedirectUrl;
            }
            if (!Utilities.IsNullOrUndefined(action.FormViewToShow))
            {
                url += url.includes("?") ? "&view=" : "?view=";
                url += action.FormViewToShow;
            }
            // This may be blocked by popup blocker if
            // 1) this wasn't directly triggered by user's action, or
            // 2) too much time has elapsed since the event was fired
            const w = window.open(url, "_blank");
            const self = this;
            if (action.FormToOpenInNewTab.Modal)
            {
                // setup blocking spinner
                self.$metadataEventService.fireRecalculateWorkingListeners(
                    {
                        ShowLoading: true,
                        Recalculating: true,
                        Message: "Awaiting action in another browser tab..."
                    },
                    true
                );
            }
            const onCloseTab = function (): void
            {
                if (action.FormToOpenInNewTab.Modal)
                {
                    self.$metadataEventService.fireRecalculateWorkingListeners(
                        {
                            ShowLoading: false,
                            Recalculating: false,
                            Message: ""
                        },
                        true
                    );
                }
                const saved = self.$storageService.Get<string>(MetadataActionService.WindowSave) === "true";
                if (action.RecalculatePropertyOnClose && (saved || isDocViewer()))
                {
                    self.doRecalculate(model, action);
                }
                self.refreshLookups(afterSaveContext?.LookupsToRefresh);
                self.$storageService.Remove(MetadataActionService.WindowSave);
                if (!Utilities.IsNullOrUndefined(afterCloseTab))
                    afterCloseTab();

            };

            if (Utilities.IsNullOrUndefined(w))
            {
                if (this.$shellService.isEmbedded())
                    this.configureOnCloseTabForShell(url, onCloseTab);
                else
                    this.$toastService.showToast(`Please allow popups from ${window.location.origin} in your browser settings."`);
            }
            else
            {
                setTimeout(function ()
                {
                    w.onbeforeunload = onCloseTab;
                }, 1000);
            }
            return true;
        }

        function isDocViewer(): boolean
        {
            return action.FormToOpenInNewTab.Name === "Content Manager" && action.FormToOpenInNewTab.Path === "Common/Content Manager";
        }

        return false;
    }

    configureOnCloseTabForShell(url: string, onCloseTab: any): void
    {
        const self = this;
        const d = document as any;
        if (Utilities.IsNullOrUndefined(d.manuallyNotifyTabWasClosedUrls))
            d.manuallyNotifyTabWasClosedUrls = [];
        d.manuallyNotifyTabWasClosedUrls[url] = onCloseTab;
        d.manuallyNotifyTabWasClosed = function (closedUrl: string)
        {
            for (const otherTabUrl in d.manuallyNotifyTabWasClosedUrls)
            {
                if (self.urlsAreEquivalent(otherTabUrl, closedUrl))
                {
                    d.manuallyNotifyTabWasClosedUrls[otherTabUrl]();
                    delete d.manuallyNotifyTabWasClosedUrls[otherTabUrl];
                    return;
                }
            }
        };
    }

    urlsAreEquivalent(urlA: string, urlB: string): boolean
    {
        const decodedUrlA = decodeURIComponent(urlA);
        const decodedUrlB = decodeURIComponent(urlB);
        return decodedUrlA.includes(decodedUrlB) || decodedUrlB.includes(decodedUrlA);
    }

    doRecalculate(model: any, action: IMetadataAction): void
    {
        const $metadataPropertyService = this.injector.get(MetadataPropertyService);
        const PropertyMetadata = <PropertyMetadata>{
            Name: action.RecalculatePropertyOnClose.PropertyToChange,
            RecalculateOnChange: $metadataPropertyService.getPropertiesToRecalculate(action.RecalculatePropertyOnClose),
            RecalculateWithParent: action.RecalculatePropertyOnClose.RecalculateWithParent,
            RecalculateDoNotSend: action.RecalculatePropertyOnClose.PropertiesToExcludeInSend,
            RecalculateToIgnore: action.RecalculatePropertyOnClose.PropertiesToIgnore
        };
        const typeName = model["$$TypeName"];
        $metadataPropertyService.recalculateProperties(
            typeName,
            PropertyMetadata,
            model,
            model["$$Parent"],
            model[action.RecalculatePropertyOnClose.PropertyToChange],
            undefined,
            false,
            true,
            <RepositoryFormData>null
        );
    }

    private refreshLookups(lookups: string[] | undefined, formData?: RepositoryDataCollection): void
    {
        if (!lookups)
            return;
        const self = this;
        lookups.forEach(type =>
        {
            const rpf = ObjectHelpers.getRepositoryFormData(formData);
            rpf?.Clear(type);
            self.$cacheHelperService.clearAllForType(type);
            self.$metadataEventService.fireLookupTypeCacheClearListener(type);
        });
    }

    // REVIEW - I think we have reached a point where metadataActions should require a RepositoryDataCollection and just dispense with passing in a model object....
    ExecuteAction(
        model: any,
        action: IMetadataAction,
        formData?: RepositoryDataCollection,
        recalculated?: boolean,
        afterSaveContext?: AfterSaveContext
    ): Promise<any>
    {
        const self = this;
        this.actionRedirecting = false;
        if (MetadataActionHelpers.isReturnPathFormReload(action))
        {
            this.$metadataEventService.fireFormReloadListeners();
            return Promise.resolve();
        }
        if (action.PreRecalculateProperty && action.FormToOpenInNewTab && !recalculated)
            return this.RecalculateIfNeeded(model, action, formData);
        if (this.OpenNewTabIfNeeded(model, action, undefined, afterSaveContext) === true) return Promise.resolve(model);
        if (this.RedirectIfNeeded(model, action) === true) return Promise.resolve(model);
        if (this.MailToIfNeeded(model, action) === true) return Promise.resolve(model);
        if (this.navigateToViewIfNeeded(model, action)) return Promise.resolve(model);
        return this.ModelToUse(action, model, formData)
            .then(modelToUse =>
            {
                let repositoryDataCollection =
                    formData && !action.SeparateFormData ? formData : self.GetNewRepositoryFormData(action, modelToUse);
                if (action.SeparateFormData && !Utilities.IsNullOrUndefinedOrEmptyObject(repositoryDataCollection) && Utilities.IsNullOrUndefinedOrEmptyObject(repositoryDataCollection.Entity))
                    repositoryDataCollection.Entity = modelToUse;
                if (action.ViewToShow?.OnInterval?.FinalAction)
                    action.ViewToShow.OnInterval.FinalAction.Context = model;
                if (this.isClientSideMetadataAction(action))
                {
                    return action.$Action(modelToUse, repositoryDataCollection);
                }
                if (action.PreRecalculateProperty && !recalculated)
                    return this.RecalculateIfNeeded(modelToUse, action, repositoryDataCollection);
                if (action.Context)
                {
                    let contextModel: any;
                    if (action.Context["$isEntityWrapper"])
                        contextModel = action.Context;
                    else
                        contextModel = JSON.parse(JSON.stringify(action.Context)); // Copy
                    if (
                        Array.isArray(contextModel) &&
                        !Utilities.IsNullOrUndefined(action.ViewToShow) &&
                        !Utilities.IsNullOrUndefined(action.ViewToShow["GridProperties"])
                    )
                    {
                        repositoryDataCollection.OtherEntities[action.ViewToShow["GridProperties"].ClassMetadata.TypeName] =
                            [];
                        if ("$OtherEntitiesByLoadFilter" in repositoryDataCollection)
                        {
                            const rpf = ObjectHelpers.getRepositoryFormData(repositoryDataCollection);
                            rpf.AddAll(
                                action.ViewToShow["GridProperties"].ClassMetadata.TypeName,
                                contextModel,
                                null
                            );
                        }
                        else
                            repositoryDataCollection.OtherEntities[
                                action.ViewToShow["GridProperties"].ClassMetadata.TypeName
                            ] = contextModel;
                    }
                    else if (!Utilities.IsNullOrUndefined(repositoryDataCollection) && !repositoryDataCollection.Entity)
                    {
                        repositoryDataCollection.Entity = contextModel;
                    }
                    modelToUse = contextModel;
                }
                return action.ViewToShow
                    ? self.showAction(action, modelToUse, repositoryDataCollection)
                        .then(processResult, onError)
                        .then(reloadViews, onError)
                    : self.dispatch(action, modelToUse, repositoryDataCollection)
                        .then(processResult, onError);
                function onError(error: any): Promise<any>
                {
                    const isError =
                        !Utilities.IsNullOrUndefined(error) &&
                        !Utilities.IsNullOrUndefined(error.error);
                    const isVerificationQuestion = isError &&
                        !Utilities.IsNullOrUndefined(error.error.VerificationId);
                    if (isVerificationQuestion)
                    {
                        const callback = (): Promise<AugmentedModelObject> =>
                            self.dispatch(action, modelToUse, repositoryDataCollection)
                                .then(processResult, onError);
                        const handler = self.GetVerificationHandler(
                            repositoryDataCollection || modelToUse,
                            callback,
                            modelToUse
                        );
                        return handler(error);
                    }
                    else if (isError)
                    {
                        const handler = self.GetVerificationHandler(
                            repositoryDataCollection || modelToUse,
                            null,
                            modelToUse
                        );
                        return handler(error);
                    }
                    console.error(`metadataActionService.executeAction | error occurred executed action`, error);
                    return Promise.reject(error);
                }
                function processResult(callValue: any): any
                {
                    if (MetadataActionHelpers.IsCancelObject(callValue))
                    {
                        return callValue;
                    }
                    if (callValue)
                    {
                        if (callValue.AfterSaveContext)
                        {
                            self.ProcessAfterSave(callValue.AfterSaveContext, model, formData);
                            if (callValue.Entity && callValue.Entity["AfterSaveContext"])
                            {
                                callValue.Entity["AfterSaveContext"].SaveConfirmationMessage = null;
                                callValue.Entity["AfterSaveContext"].SaveConfirmationHeader = null;
                                callValue.Entity["AfterSaveContext"].DocumentsToDownload = null;
                            }
                        }
                        else self.ProcessAfterSave(callValue, model, formData);
                    }
                    if (!MetadataActionHelpers.isReturnPathDocument(action)) //Prevent double download while return path still exists
                        self.DownloadAnyReturnedFiles(callValue);
                    if (action.RecalculatePropertyOnClose)
                    {
                        if (action.RecalculatePropertyOnClose)
                        {
                            // This is a specific use case for resequence actions as they are currently written if we find ourselves needing more functionality here
                            // It would probably be worth just refactoring resequence actions to make more sense with our current structure.
                            if (MetadataActionHelpers.isResequenceAction(action))
                            {
                                return self.$repositoryService
                                    .RecalculateGridView({
                                        Item1: repositoryDataCollection,
                                        Item2: action.RowType,
                                    })
                                    .then(rdc =>
                                    {
                                        return self.ProcessAfterSave(rdc.AfterSaveContext, rdc.Entity, rdc).then(x =>
                                        {
                                            const rpf = ObjectHelpers.getRepositoryFormData(repositoryDataCollection);
                                            rpf.Merge(rdc);
                                            self.$gridEventService.fireRefreshGridListeners();
                                            self.$metadataService.FireGridReload(action.RowType, false);
                                        });
                                    });
                            }
                            else
                            {
                                // TODO - Fix the circular dependency between this service and the MetadataPropertyService.
                                const $metadataPropertyService = self.injector.get(MetadataPropertyService);
                                const PropertyMetadata = <PropertyMetadata>{
                                    Name: action.RecalculatePropertyOnClose.PropertyToChange,
                                    RecalculateOnChange: $metadataPropertyService.getPropertiesToRecalculate(action.RecalculatePropertyOnClose, true),
                                    RecalculateWithParent: action.RecalculatePropertyOnClose.RecalculateWithParent,
                                    RecalculateDoNotSend: action.RecalculatePropertyOnClose.PropertiesToExcludeInSend,
                                    RecalculateToIgnore: action.RecalculatePropertyOnClose.PropertiesToIgnore
                                };
                                // Since there is a recalc, assume the return path is this or some property path
                                // Merge the result before firing the next recalc
                                if (MetadataActionHelpers.isReturnPathThis(action))
                                {
                                    Utilities.merge(model, callValue);
                                }
                                else if (
                                    action.ReturnPath &&
                                    !MetadataActionHelpers.isReturnPathDocument(action) &&
                                    !MetadataActionHelpers.isReturnPathResults(action) &&
                                    !MetadataActionHelpers.isReturnPathBack(action) &&
                                    !MetadataActionHelpers.isReturnPathReload(action)
                                )
                                {
                                    self.$metadataService.SetModuleValue(action.ReturnPath, null, model, callValue);
                                }
                                const formType = !Utilities.IsNullOrUndefined(formData) ? formData.Type : null;
                                const typeName = modelToUse["$$TypeName"] ? modelToUse["$$TypeName"] : formType;
                                if (!Utilities.IsNullOrUndefined(typeName))
                                {
                                    return $metadataPropertyService.recalculateProperties(
                                        typeName,
                                        PropertyMetadata,
                                        modelToUse,
                                        modelToUse["$$Parent"],
                                        modelToUse[action.RecalculatePropertyOnClose.PropertyToChange],
                                        undefined,
                                        false,
                                        true,
                                        <RepositoryFormData>repositoryDataCollection
                                    );
                                }
                            }
                        }
                    }
                    if (action.ReturnPath)
                    {
                        if (MetadataActionHelpers.isReturnPathThis(action))
                        {
                            Utilities.merge(model, callValue);
                            self.$metadataService.FireGridReload(model.$$TypeName, true);
                            self.$metadataEventService.fireRecalculateWorkingListeners(
                                {
                                    ShowLoading: false,
                                    Recalculating: false,
                                },
                                false
                            );
                        }
                        else if (MetadataActionHelpers.isReturnPathDocument(action))
                        {
                            self.DownloadAnyReturnedFiles(callValue);
                            return;
                        }
                        else if (MetadataActionHelpers.isReturnPathResults(action))
                        {
                            return callValue;
                        }
                        else if (MetadataActionHelpers.isReturnPathBack(action))
                        {
                            self.actionRedirecting = true;
                            window.history.back();
                        }
                        else if (MetadataActionHelpers.isReturnPathReload(action))
                        {
                            window.location.reload();
                        }
                        else if (MetadataActionHelpers.isReturnPathCloseDialog(action))
                        {
                            self.closeDialogAction = true;
                            self.$metadataEventService.fireSaveAfterRecalculateListener();
                        }
                        else if (MetadataActionHelpers.isOtherEntities(action.ReturnPath))
                        {
                            if (!repositoryDataCollection)
                                repositoryDataCollection = model["$FormData"];
                            const rowType = action.ReturnPath.replace("OtherEntities.", '');
                            const currentOtherEntity = repositoryDataCollection.OtherEntities[rowType];
                            if (!Utilities.IsNullOrUndefined(currentOtherEntity) && (Utilities.IsNullOrUndefined(callValue["$$TypeName"]) || callValue["$$TypeName"] === rowType))
                            {
                                repositoryDataCollection.OtherEntities[rowType] = callValue;
                            }
                            else
                            {
                                const rpf = ObjectHelpers.getRepositoryFormData(repositoryDataCollection);
                                rpf.Clear(rowType);
                                self.$cacheHelperService.clearAllForType(rowType);
                                self.$metadataService.FireGridRefetch(rowType);
                                self.$metadataEventService.fireLookupTypeCacheClearListener(rowType);
                            }
                        }
                        else
                        {
                            self.$metadataService.SetModuleValue(action.ReturnPath, null, model, callValue);
                            self.$metadataService.FireGridReload(model.$$TypeName, true);
                        }
                        return modelToUse;
                    }
                    if (callValue && callValue.Entity && callValue.Type)
                    {
                        Utilities.merge(modelToUse, callValue.Entity);
                        self.$metadataService.FireGridReload(modelToUse, true);
                        return modelToUse;
                    }
                    return callValue;
                }

                function reloadViews(callValue: any): any
                {
                    self.$metadataEventService.fireLookupTypeCacheClearListener(action.ViewToShow.ViewMetaData?.TypeName);
                    return callValue;
                }
            });
    }

    RecalculateIfNeeded(model: AugmentedModelObject, action: IMetadataAction, formData?: RepositoryDataCollection): Promise<any>
    {
        const $metadataPropertyService = this.injector.get(MetadataPropertyService);
        const PropertyMetadata: PropertyMetadata = {
            Name: action.PreRecalculateProperty.PropertyToChange,
            RecalculateOnChange: $metadataPropertyService.getPropertiesToRecalculate(action.PreRecalculateProperty),
            RecalculateWithParent: action.PreRecalculateProperty.RecalculateWithParent,

        };
        const formType = formData?.Type ?? null;
        const typeName = model?.$$TypeName
            ? model.$$TypeName
            : formType;
        return $metadataPropertyService.recalculateProperties(
            typeName,
            PropertyMetadata,
            model,
            model?.$$Parent,
            model
                ? model[action.PreRecalculateProperty.PropertyToChange]
                : undefined,
            undefined,
            false,
            false,
            RepositoryFormData.create(formData)
        )
            .then(r =>
            {
                if (Utilities.IsNullOrUndefined(r?.Halt) || r.Halt === false)
                    return this.ExecuteAction(model, action, formData, true);
                return Promise.resolve();
            });
    }

    DownloadAnyReturnedFiles(model: any): void
    {
        const downloadableContent = MetadataActionHelpers.GetDocumentsFromModel(model);
        if (Utilities.IsNullOrUndefined(downloadableContent)) return;
        const self = this;
        downloadableContent.forEach(doc =>
        {
            self.handleDocument(doc);
        });
    }

    IsDisabled(action: IMetadataAction, model: any, parent?: any): boolean
    {
        return Utilities.IsNullOrUndefined(action.Disabled)
            ? action.DisabledProperty && this.$metadataService.EvaluateBoolean(action.DisabledProperty, parent, model)
            : action.Disabled;
    }

    IsHidden(action: IMetadataAction, model: any, parent?: any): boolean
    {
        return Utilities.IsNullOrUndefined(action.Hidden)
            ? action.HiddenProperty && this.$metadataService.EvaluateBoolean(action.HiddenProperty, parent, model)
            : action.Hidden;
    }

    /**
     * Finds the model object for the given `action` relative to the given `model`.
     * @param action IMetadataAction
     * @param model object
     */
    ModelToUse(action: IMetadataAction, model: any, dataCollection: RepositoryDataCollection): Promise<any>
    {
        const self = this;
        let modelResult = null;
        if (action.Context)
        {
            modelResult = null;
        }
        else if (!action.ModelPath || MetadataActionHelpers.isModelPathThis(action))
        {
            if (model !== undefined)
            {
                modelResult = model;
            }
            else if (!Utilities.IsNullOrUndefined(dataCollection))
            {
                modelResult = dataCollection.Entity;
            }
            else
            {
                console.log("No model present on metadata action.");
            }
        }
        else if (MetadataActionHelpers.isModelPathNewObject(action))
        {
            const newObject = {};
            modelResult = newObject;
        }
        else if (MetadataActionHelpers.isModelPathRepositoryNew(action))
        {
            const rdc = this.GetNewRepositoryFormData(action, null);
            const type = Utilities.IsNullOrUndefined(rdc) ? dataCollection.Type : rdc.Type;
            const filter = this.$metadataViewService.GetFilterFromView(model, action.ViewToShow);
            const rejectionHandler = this.GetVerificationHandler(
                dataCollection,
                () => self.ModelToUse(action, model, dataCollection),
                model
            );
            const getNewModel = JSON.stringify({ ConfirmationQuestions: dataCollection ? dataCollection.ConfirmationQuestions : {} });
            return this.$repositoryService
                .GetNew(
                    {
                        Item1: type,
                        Item2: getNewModel,
                    },
                    filter
                )
                .then(model =>
                {
                    model["$$TypeName"] = type;
                    return model;
                }, rejectionHandler);
        }
        else if (MetadataActionHelpers.isModelPathOtherEntities(action))
        {
            const pattern = /^OtherEntities\.([\w.]*)(?:\[(\d+)\])?\.?([\w.]*)?/;
            const matches = pattern.exec(action.ModelPath);
            const type = matches[1];
            const index = matches[2];
            const child = matches[3];
            if (Utilities.IsNullOrUndefinedOrEmptyObject(dataCollection.OtherEntities))
            {
                modelResult = model;
            }
            else
            {
                modelResult = index
                    ? child
                        ? dataCollection.OtherEntities[type][index][child]
                        : dataCollection.OtherEntities[type][index]
                    : dataCollection.OtherEntities[type];
            }
        }
        else if (MetadataActionHelpers.isModelPathParent(action))
        {
            modelResult = model["$$Parent"];
        }
        else if (action.ModelPath)
        {
            modelResult = this.drillIntoModel(model, action.ModelPath);
        }
        return Promise.resolve(modelResult);
    }

    /**
     * Attempts to traverse the given `currentModel` using the given `path` to find the targeted value.
     * @param currentModel object
     * @param path path relative to `currentModel` that contains the next modelValue.
     */
    private drillIntoModel(currentModel: any, path: string): any
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(path)) return currentModel;
        const pathNodes = path.split(".");
        const followingNodes = pathNodes.length > 1 ? pathNodes.slice(1)
            .join(".") : null;
        const currentNode = pathNodes[0];
        if (Utilities.IsNullOrUndefined(currentModel[currentNode])) return {};
        if (!Array.isArray(currentModel[currentNode]))
            return this.drillIntoModel(currentModel[currentNode], followingNodes);
        const values = [];
        for (let i = 0; i < currentModel[currentNode].length; i++)
        {
            const element = currentModel[currentNode][i];
            const elementVals = this.drillIntoModel(element, followingNodes);
            this.pushFlattenedElementToArray(elementVals, values);
        }
        return values;
    }

    private pushFlattenedElementToArray(element: any, array: any[]): void
    {
        if (Array.isArray(element))
        {
            array.push(...element);
        }
        else
        {
            array.push(element);
        }
    }

    actionDialogActive: boolean = false;

    private showAction(
        action: IMetadataAction,
        model: any,
        repositoryCollection: RepositoryDataCollection
    ): Promise<any>
    {
        this.activeDialogIndex++;

        const self = this;
        const dialogConfig = <MatDialogConfig<MetadataActionDialogData>>{
            data: {
                action: action,
                model: model,
                datacontroller: ObjectHelpers.getRepositoryFormData(repositoryCollection),
                beforeComplete: beforeComplete,
                dialogIndex: this.activeDialogIndex
            }
        };

        this.actionDialogActive = true;
        const dialogRef = this.matDialog.open(MetadataActionDialogComponent, dialogConfig);
        return Promise.resolve(dialogRef
            .afterClosed()
            .toPromise())
            .then(
                x =>
                {
                    if (MetadataActionHelpers.IsCancelObject(x)) clearSubPropertyAction(action, repositoryCollection);
                    this.actionDialogActive = false;
                    return x;
                },
                _x =>
                {
                    clearSubPropertyAction(action, repositoryCollection);
                    return Promise.reject();
                }
            )
            .catch(_r =>
            {
                /* Squashing errors */
            })
            .finally(() =>
            {
                this.activeDialogIndex--;
                if (this.activeDialogIndex < 0)
                {
                    this.activeDialogIndex = 0;
                }
            }
            );
        function beforeComplete(
            action: IMetadataAction,
            newModel: any,
            dataCollection: RepositoryDataCollection
        ): Promise<any>
        {
            newModel = self.applySelected(action, newModel);
            const iframeWindow = self.$metadataIframeService.FindMetadataWindowHostedInIframe(action, newModel);

            if (iframeWindow)
            {
                // If we are closing an iframe view, and it's hosting a metadata form, we will post a message to trigger the complete/save command.
                // If callback is received, continue with the rest. If timed out, stop the closing logic assuming some error occurred in the inner metadata form.
                return beforeCompleteIFrameAction(iframeWindow, action, newModel, dataCollection);

            }
            else
            {
                return beforeCompleteAction(action, newModel, dataCollection);
            }
        }

        function beforeCompleteIFrameAction(
            contentWindow: Window,
            action: IMetadataAction,
            newModel: any,
            dataCollection: RepositoryDataCollection): Promise<any>
        {
            const message: KeyValuePair<string, any> =
            {
                Key: "metadataIframeCloseRequest",
                Value: null
            };
            // This will call the iframe view to save and close, which may trigger multiple verification questions, and need some extra time
            // Wait for up to 8 minutes until the embedded view to respond.
            const timeout = 8 * 60 * 1000;
            return self.$metadataIframeService.PostMessageToIframeAndGetResponse(contentWindow, message, "metadataIframeCloseResponse", timeout)
                .then(messageEvent =>
                {
                    if (messageEvent && messageEvent.data.Value === true)
                    {
                        return beforeCompleteAction(action, newModel, dataCollection);
                    }
                    else
                    {
                        return Promise.reject("External form failed to process the complete request.");
                    }
                });
        }
        function beforeCompleteAction(
            action: IMetadataAction,
            newModel: any,
            dataCollection: RepositoryDataCollection
        ): Promise<any>
        {
            if (action.PreCloseRecalculateProperty)
            {
                const $metadataPropertyService = self.injector.get(MetadataPropertyService);
                const PropertyMetadata = <PropertyMetadata>{
                    Name: action.PreCloseRecalculateProperty.PropertyToChange,
                    RecalculateOnChange: $metadataPropertyService.getPropertiesToRecalculate(action.PreCloseRecalculateProperty),
                    RecalculateWithParent: action.PreCloseRecalculateProperty.RecalculateWithParent,
                    RecalculateDoNotSend: action.PreCloseRecalculateProperty.PropertiesToExcludeInSend,
                    RecalculateToIgnore: action.PreCloseRecalculateProperty.PropertiesToIgnore
                };
                const typeName = newModel["$$TypeName"];
                return $metadataPropertyService.recalculateProperties(
                    typeName,
                    PropertyMetadata,
                    newModel,
                    newModel["$$Parent"],
                    newModel[action.PreCloseRecalculateProperty.PropertyToChange],
                    undefined,
                    false,
                    true,
                    <RepositoryFormData>dataCollection
                ).then(r => self.dispatch(action, newModel, dataCollection));
            }
            return self.dispatch(action, newModel, dataCollection);
        }
        function clearSubPropertyAction(action: IMetadataAction, repositoryCollection: RepositoryDataCollection): void
        {
            if (!action.ClearModelOnCancel) return;
            const property = action.ViewToShow.SubPropertyName;
            if (property)
            {
                const entity = action.ViewToShow.SubPropertyType
                    ? repositoryCollection.OtherEntities[action.ViewToShow.SubPropertyType][0]
                    : repositoryCollection.Entity;
                if (entity[property]) entity[property] = null;
            }
        }
    }

    applySelected(action: IMetadataAction, model: any): any
    {
        if (MetadataActionHelpers.isMultiSelectAction(action))
        {
            const contextKey = action.ContextKey;
            if (contextKey in model)
                return model[contextKey].filter(x => x["Selected"]);
        }
        return model;
    }

    /**
     * Decides how an action is too be handled.
     *
     * @private Only available within the `@tylertech/metadata-module`
     *
     * @param action IMetadataAction
     * @param model object
     * @param formData RepositoryFormData
     */
    dispatch(action: IMetadataAction, model: any, formData: RepositoryDataCollection): Promise<any>
    {
        if (MetadataActionHelpers.isIntentAction(action))
        {
            if (action.IsRefresh)
            {
                if (this.delayTabClose)
                {
                    return this.delayRefresh();
                }
                window.location.reload();
                return Promise.resolve();
            }
            if ("IntentURI" in action && !Utilities.IsNullOrUndefinedOrEmpty(action["IntentURI"]))
            {
                return this.$intentService.resolveURI(action.IntentURI);
            }
            if ("IntentMime" in action)
            {
                let data = null;
                if ("IntentData" in action)
                {
                    data = Object.assign({}, action["IntentData"]);
                    for (const key of Object.keys(action["IntentData"]))
                    {
                        data[key] = this.$metadataService.DependentModuleValue(data[key], null, model);
                    }
                }
                return this.$intentService.resolve(action["IntentMime"], action["IntentAction"], data);
            }
        }
        if (MetadataActionHelpers.isResequenceAction(action))
        {
            return this.executeResequenceAction(action, model);
        }
        const saveAll = MetadataActionHelpers.isRepositorySaveAll(action);
        if (Utilities.IsNullOrUndefinedOrEmpty((<IMetadataAction>action).ServicePath) || saveAll)
        {
            return this.save(model, formData, saveAll)
                .then(r =>
                {
                    return r;
                }, this.getErrorHandler(formData));
        }
        if (MetadataActionHelpers.isNoServicePath(action))
        {
            return Promise.resolve(model);
        }
        if (MetadataActionHelpers.isRequiredValid(action))
        {
            let verifyData = formData;
            if ((<IMetadataAction>action).VerifyWithParent)
            {
                const parentReference = model["$$Parent"];
                if (!Utilities.IsNullOrUndefined(parentReference))
                {
                    verifyData = Utilities.deepCopy(formData);
                    verifyData.OptionalParentEntity = parentReference;
                    verifyData.OptionalParentType = parentReference["$$TypeName"];
                }
            }
            return this.verify(model, verifyData);
        }
        // Else throw it all a the service path
        return this.customServicePath(action, model, formData)
            .then(r =>
            {
                return r;
            }, this.getErrorHandler(formData));
    }

    getValue(model: any, property: PropertyMetadata): string
    {
        if (property.InputType === "Date")
        {
            return formatDate(model[property.Name], "MM/dd/yyyy", this.locale);
        }
        if (property.InputType === "Currency")
        {
            return formatCurrency(parseFloat(model[property.Name]), this.locale, "$", null);
        }
        return model[property.Name];
    }

    private executeResequenceAction(action: IMetadataResequenceAction, model: any[]): Promise<any>
    {
        const self = this;
        const titleProperty = Utilities.IsNullOrUndefined(action.TitleProperty)
            ? action.SequenceProperty.Name
            : action.TitleProperty.Name;
        const hasGroup = !Utilities.IsNullOrUndefined(action.GroupProperty);
        const hasSubtitle = !Utilities.IsNullOrUndefined(action.SubtitleProperty);
        if (!hasGroup)
        {
            model = model.sort((a, b) => (a[action.SequenceProperty.Name] < b[action.SequenceProperty.Name] ? -1 : 1));
        }
        model.forEach(m =>
        {
            m[`$${titleProperty}`] = this.getValue(m, action.TitleProperty);
            if (hasSubtitle) m[`$${action.SubtitleProperty.Name}`] = this.getValue(m, action.SubtitleProperty);
        });
        function update(result: any[]): void
        {
            result.forEach((item, index) =>
            {
                const originalIndex = item[action.SequenceProperty.Name];
                const newIndex = index + 1;
                if (originalIndex !== newIndex && item.SerializedRowState !== "Added")
                    item.SerializedRowState = "Modified";
                item[action.SequenceProperty.Name] = newIndex;
                delete item[`$${titleProperty}`];
                if (hasSubtitle) delete item[`$${action.SubtitleProperty.Name}`];
            });
        }
        function onSave(result: any[][]): boolean
        {
            if (!hasGroup) update(result[0]);
            else result.forEach(i => update(i["Items"]));
            self.$gridEventService.fireRefreshGridListeners();
            self.$metadataService.FireGridReload(action.RowType, false);
            return true;
        }
        function onCancel(_r: any): void
        {
            model.forEach(m =>
            {
                delete m[`$${titleProperty}`];
                if (hasSubtitle) delete m[`$${action.SubtitleProperty.Name}`];
            });
        }
        if (hasGroup)
        {
            return self.$commonDialogsService.showDragAndDropGroupDialog(
                [model],
                action.GroupProperty.Name,
                `$${titleProperty}`,
                hasSubtitle ? `$${action.SubtitleProperty.Name}` : null,
                action.Label,
                "",
                action.SequenceProperty.Name
            )
                .then(onSave, onCancel);
        }
        else
        {
            return self.$commonDialogsService.showDragAndDropDialog(
                [model],
                `$${titleProperty}`,
                hasSubtitle ? `$${action.SubtitleProperty.Name}` : null,
                action.Label,
                ""
            )
                .then(onSave, onCancel);
        }
    }

    /**
     * Sends the given `model` or `dataCollection` to the appropriate save endpoint in the `IRepositoryService`.
     * @param model object
     * @param dataCollection RepositoryDataCollection
     */
    private save(
        model: any,
        dataCollection: RepositoryDataCollection,
        saveAll: boolean
    ): Promise<RepositoryDataCollection>
    {
        const self = this;
        //All metadata actions with repo handlers want to return data on save.
        dataCollection.ReturnDataOnSave = true;
        if (dataCollection.Entity === null && dataCollection.Type !== null && !saveAll)
        {
            return self.$repositoryService
                .Save({
                    Item1: dataCollection.Type,
                    Item2: Utilities.stringify(model),
                })
                .then(returned =>
                {
                    dataCollection.Entity = returned;
                    return dataCollection;
                });
        }
        const rejectionHandler = this.GetVerificationHandler(
            dataCollection,
            () => self.save(model, dataCollection, saveAll),
            model
        );
        function clearQuestions(saveResults: RepositoryDataCollection): RepositoryDataCollection
        {
            dataCollection.ConfirmationQuestions = {};
            return saveResults;
        }
        dataCollection.Entity = model;
        return self.$repositoryService.SaveAll(dataCollection)
            .then(clearQuestions, rejectionHandler);
    }

    /**
     * Sends the given `model` or `dataCollection` to the appropriate verify endpoint in the `IRepositoryService`.
     * @param model object
     * @param dataCollection RepositoryDataCollection
     */
    private verify(model: any, dataCollection: RepositoryDataCollection): Promise<any>
    {
        const self = this;
        if (dataCollection.Entity === null && dataCollection.Type !== null)
        {
            return this.$repositoryService
                .Verify({
                    Item1: dataCollection.Type,
                    Item2: Utilities.stringify(model),
                })
                .then(r => self.processVerificationResults(r, model, dataCollection));
        }
        const rejectionHandler = this.GetVerificationHandler(
            dataCollection,
            () => self.verify(model, dataCollection),
            model
        );
        return this.$repositoryService
            .VerifyAll(dataCollection)
            .then(r => self.processVerificationResults(r, model, dataCollection), rejectionHandler);
    }

    private processVerificationResults(
        r: MetadataVerification | MetadataCollectionVerification,
        model: any,
        dataCollection: RepositoryDataCollection
    ): Promise<any>
    {
        this.$metadataErrorService.setErrors(r, dataCollection);
        if (r.IsValid)
            return Promise.resolve(model);

        const errors = this.$metadataErrorService.getErrorList(r);
        const message = errors.reduce((p, x) => `${p}<li>${x}</li>`, "");
        this.$gridEventService.fireFetchTable();
        this.$metadataIframeService.MaybeResponseToParentWindow(false);
        const handler = (): Promise<any> =>
        {
            return Promise.reject();
        };

        return this.$commonDialogsService.showAlertMessage(message, "Verification Errors")
            .then(handler, handler);
    }

    /**
     * Sends the given `model` to the endpoint defined in the action's ServicePath.
     * @param action IMetadataAction
     * @param model object
     * @param dataCollection RepositoryFormData
     */
    private customServicePath(
        action: IMetadataAction,
        model: any,
        dataCollection: RepositoryDataCollection
    ): Promise<any>
    {
        const self = this;
        const formData = ObjectHelpers.isRepositoryFormData(dataCollection)
            ? dataCollection
            : RepositoryFormData.create(dataCollection);
        const dataCollectionAction = this.sendDataCollection(action);
        const serviceParam = dataCollectionAction ? dataCollection : model;
        const rejectionHandler = dataCollectionAction
            ? this.GetVerificationHandler(formData, () => self.customServicePath(action, model, formData), model)
            : this.getErrorHandler(formData);
        const filterString = !Utilities.IsNullOrUndefined(action.ConstantFilter) && !Utilities.IsNullOrUndefined(model)
            ? this.$metadataService.EvaluateString(action.ConstantFilter, model.$$Parent, model, null, true)
            : null;
        return this.$apiService
            .sendAuth(action.ServicePath, serviceParam, filterString)
            .then(processServiceResponse, rejectionHandler);
        function processServiceResponse(x: any): unknown
        {
            if (!Utilities.IsNullOrUndefined(dataCollection))
            {
                dataCollection.ConfirmationQuestions = {};
            }
            // if an action is returned chain to that action
            if (MetadataActionHelpers.isAction(x))
            {
                const data = <RepositoryFormData>x.Context;
                if (data && data.Entity && data.Type)
                {
                    if (!Utilities.IsNullOrUndefined(model))
                    {
                        Object.assign(model, data.Entity);
                    }
                    formData.Merge(data);
                }
                x.ViewOnly = action.ViewOnly;
                if (self.$multipleEntityEditService.maybeDelayAddSetup && x.ViewToShow)
                    self.$multipleEntityEditService.maybeDelayAddSetup = false;
                return self.ExecuteAction(model, x, formData);
            }
            if (dataCollection && dataCollection.Type != null)
                self.processVerificationResults(
                    <MetadataVerification>{
                        IsValid: true,
                        FieldErrors: {},
                        Type: dataCollection.Type,
                    },
                    model,
                    dataCollection
                );
            if (ObjectHelpers.isDataCollection(x) && !self.skipMerge(action) && x.Entity && x.Type)
            {
                formData.Merge(x);
            }
            return x;
        }
    }

    private skipMerge(action: IMetadataAction): boolean
    {
        return MetadataActionHelpers.isDataCollectionAction(action) && action.SkipMerge;
    }

    private sendDataCollection(action: IMetadataAction): boolean
    {
        return MetadataActionHelpers.isDataCollectionAction(action) && action.SendDataCollectionToService;
    }

    /**
     * Returns a function that when given a value, will determine if it is an error representing a
     * MetadataVerificationException. If so, then it will update the given formData with the verification
     * messages. If the error value is some other type, then it is re-thrown.
     *
     * @param dataCollection RepositoryDataCollection
     */
    private getErrorHandler(dataCollection: RepositoryDataCollection): (e) => void
    {
        const self = this;
        return handleVerificationError;
        function handleVerificationError(e): void
        {
            if (e && e.AdditionalInfo)
            {
                self.$metadataErrorService.setErrors(JSON.parse(e.AdditionalInfo), dataCollection);
            }
            throw e;
        }
    }

    navigateToViewIfNeeded(model: any, action: IMetadataAction): boolean
    {
        if (Utilities.IsNullOrUndefined(action.FormViewToShow))
            return false;
        if (MetadataActionHelpers.isFormViewBack(action))
        {
            this.doRecalculate(model, action);
            window.history.back();
        }
        else if (MetadataActionHelpers.isModelPathTabbedView(action))
            this.$metadataEventService.fireNavigateTabbedViewListeners(action.FormViewToShow);
        else
            this.$metadataEventService.fireNavigateViewListeners(action.FormViewToShow);
        return true;
    }

    /**
     * MetadataPropertyService depends on the this, and is forcing us to HACK around the angularjs framework due to the circular references.
     * @deprecated This should live somewhere else.
     */
    ProcessAfterSave(afterSave: AfterSaveContext, model: any, formData?: RepositoryDataCollection): Promise<any>
    {
        const self = this;
        if (afterSave)
        {
            if (afterSave.DocumentsToDownload && afterSave.DocumentsToDownload.length > 0)
            {
                let documentViewerMessage;
                if (afterSave.SaveConfirmationMessage && !afterSave.PostAction)
                {
                    documentViewerMessage = afterSave.SaveConfirmationMessage;
                    afterSave.SaveConfirmationMessage = undefined;
                }
                afterSave.DocumentsToDownload.forEach(doc =>
                {
                    self.handleDocument(doc, documentViewerMessage, afterSave);
                });
                afterSave.DocumentsToDownload = null;
            }
            if (afterSave.LookupsToRefresh && !afterSave.PostAction?.FormToOpenInNewTab)
            {
                self.refreshLookups(afterSave.LookupsToRefresh, formData);
                afterSave.LookupsToRefresh = null;
            }
            if (afterSave.SaveConfirmationMessage)
            {
                if (this.showAsToast(afterSave))
                {
                    this.$toastService.showToast(afterSave.SaveConfirmationMessage);
                    afterSave.SaveConfirmationMessage = null;
                    afterSave.SaveConfirmationHeader = null;
                    if (afterSave.PostAction)
                    {
                        return self.ExecuteAction(model, afterSave.PostAction, formData, undefined, afterSave);
                    }
                    return Promise.resolve();
                }
                else
                {
                    return self.$commonDialogsService.showAlertMessage(
                        afterSave.SaveConfirmationMessage.replace(/(?:\r\n|\r|\n)/g, "<br>"),
                        Utilities.IsNullOrUndefined(afterSave.SaveConfirmationHeader)
                            ? "Confirmation"
                            : afterSave.SaveConfirmationHeader, "Acknowledge", "OK")
                        .then(() =>
                        {
                            afterSave.SaveConfirmationMessage = null;
                            afterSave.SaveConfirmationHeader = null;
                            if (afterSave.PostAction)
                            {
                                return self.ExecuteAction(model, afterSave.PostAction, formData, undefined, afterSave);
                            }
                            return Promise.resolve();
                        })
                        .catch(_r =>
                        {
                            /* Squashing errors */
                        });
                }
            }
            else if (afterSave.PostAction)
            {
                return self.ExecuteAction(model, afterSave.PostAction, formData, undefined, afterSave);
            }
        }
        return Promise.resolve();
    }

    handleDocument(doc: IDocument, SaveConfirmationMessage?: string, parentAfterSaveContext?: AfterSaveContext): void
    {
        if (!doc)
            return;
        if (this.openDocumentInNewTab(doc))
        {
            if (this.isOverrideSaveAction)
                this.delayTabClose = true;
            const viewerUrl = this.$fileService.getDocumentLink(doc);
            let encodedUrl = encodeURIComponent(viewerUrl);
            encodedUrl = btoa(encodedUrl);

            const split = window.location.pathname.split("/");
            let url = `/${split[1].replace(/[^a-zA-Z0-9_-]/g, "")}/apps/form/?path=Common/Document Viewer&n=true&$filter=ViewerUrl eq '${encodedUrl}' and FileName eq '${encodeURIComponent(encodeURIComponent(doc.Filename))}'`;

            if (SaveConfirmationMessage)
            {
                url += ` and SaveConfirmationMessage eq '${encodeURIComponent(SaveConfirmationMessage)}'`;
                if (this.showAsToast(parentAfterSaveContext))
                    url += ` and MessageAsToast eq 'true'`;
            }
            if (doc.ReferenceId)
                url += ` and ReferenceId eq '${doc.ReferenceId}'`;
            const self = this;
            const w = window.open(url, "_blank");
            this.$storageService.Remove(MetadataActionService.DocViewerLoaded);
            if (Utilities.IsNullOrUndefined(w) && !this.$shellService.isEmbedded())
            {
                window.URL.revokeObjectURL(viewerUrl);
                this.$fileService.openDocument(doc);
                this.$toastService.showToast(`Please allow popups from ${window.location.origin} in your browser settings."`);
                this.docViewerLoadHandler();
            }
            else if (!Utilities.IsNullOrUndefined(w))
            {
                setTimeout(function ()
                {
                    w.onbeforeunload = function () {
                        self.delayTabClose = false;
                    };
                }, 1000);
            }
            doc.Content = undefined;
        }
        else
        {
            if (parentAfterSaveContext && SaveConfirmationMessage)
                parentAfterSaveContext.SaveConfirmationMessage = SaveConfirmationMessage;
            this.$fileService.openDocument(doc);
        }
    }

    openDocumentInNewTab(doc: IDocument): boolean  //TODO: Maybe we change, but UX groupd agreed to always do this for PDFs for the time being
    {
        return doc.MimeType === "application/pdf" && doc.Content.length < 200_000_000;
    }

    delayRefresh(): Promise<void>
    {
        return new Promise<void>((resolve) =>
        {
            let attempts = 0;
            const interval = setInterval(() =>
            {
                const loaded = this.$storageService.Get<string>(MetadataActionService.DocViewerLoaded) === "true";
                if (loaded || ++attempts >= 30)
                {
                    this.$storageService.Remove(MetadataActionService.DocViewerLoaded);
                    clearInterval(interval);
                    this.delayTabClose = false;
                    window.location.reload();
                    resolve();
                }
            }, 1000);
        });
    }

    docViewerLoadHandler(): void
    {
        this.$storageService.Set(MetadataActionService.DocViewerLoaded, "true");
    }

    showAsToast(afterSave: AfterSaveContext): boolean
    {
        if (Utilities.IsNullOrUndefined(afterSave.ShowAsToast))
        {
            if (afterSave.SaveConfirmationMessage && afterSave.SaveConfirmationMessage.indexOf("\r\n") > -1)
                return false;
            if (afterSave.PostAction && (afterSave.PostAction.RedirectToForm || MetadataActionHelpers.isReturnPathBack(afterSave.PostAction) || MetadataActionHelpers.isReturnPathReload(afterSave.PostAction)))
                return false;
            if (afterSave.Halt)
                return true;
            return false;
        }
        else if (afterSave.ShowAsToast)
            return true;
        return false;
    }

    GetVerificationHandler(
        dataCollection: RepositoryDataCollection | any,
        callback: () => Promise<any>,
        model?: any
    ): (e: HttpErrorResponse) => Promise<any>
    {
        const self = this;
        function onError(): Promise<any>
        {
            self.ClearVerificationQuestions(dataCollection);
            return Promise.reject();
        }
        function onSuccess(errorInfo: ErrorInfo, response: boolean): Promise<any>
        {
            if (errorInfo.ForceStop || (!response && !errorInfo.ContinueOnNo)) return onError();
            if (!dataCollection.ConfirmationQuestions) dataCollection.ConfirmationQuestions = {};
            dataCollection.ConfirmationQuestions[errorInfo.VerificationId] = response;
            if (!Utilities.IsNullOrUndefined(model))
            {
                if (!model.ConfirmationQuestions) model.ConfirmationQuestions = {};
                model.ConfirmationQuestions[errorInfo.VerificationId] = response;
            }
            return callback()
                .then(value =>
                {
                    self.ClearVerificationQuestions(dataCollection);
                    return Promise.resolve(value);
                });
        }
        return function getVerificationHandler(x: HttpErrorResponse): Promise<any>
        {
            if (Utilities.IsNullOrUndefined(x) || Utilities.IsNullOrUndefined(x.error)) return onError();
            const errorInfo: ErrorInfo = x.error;
            let title: string = "Error";
            let message: string = errorInfo.Message?.replace(/(?:\r\n|\r|\n)/g, "<br>");
            if (errorInfo.VerificationId && dataCollection)
            {
                if (errorInfo.Action)
                {
                    const action = JSON.parse(errorInfo.Action);
                    const modelToUse = model ? model : dataCollection.Entity;
                    if (errorInfo.PropertiesToUpdate)
                    {
                        for (const property in errorInfo.PropertiesToUpdate)
                        {
                            const prop = JSON.parse(errorInfo.PropertiesToUpdate[property] as string);
                            if (property.startsWith("OtherEntities"))
                            {
                                //this should almost NEVER be used. Very rare edge case
                                const pattern = /^OtherEntities\.([\w.]*)(?:\[(\d+)\])?\.?([\w.]*)?/;
                                const matches = pattern.exec(property);
                                const type = matches[1];
                                const index = matches[2];
                                const propertyName = matches[3];

                                if (Array.isArray(prop) && !Utilities.IsNullOrUndefined(modelToUse[property]))
                                {
                                    const array = dataCollection.OtherEntities[type][index][propertyName];
                                    array.length = 0;
                                    array.push(...prop);
                                }
                                else
                                    dataCollection.OtherEntities[type][index][propertyName] = JSON.parse(errorInfo.PropertiesToUpdate[property] as string);
                            }
                            else
                            {
                                if (Array.isArray(prop) && !Utilities.IsNullOrUndefined(modelToUse[property]))
                                {
                                    const array = modelToUse[property];
                                    array.length = 0;
                                    array.push(...prop);
                                }
                                else
                                    modelToUse[property] = JSON.parse(errorInfo.PropertiesToUpdate[property] as string);
                            }
                        }
                    }
                    action.$fromVQH = true;
                    return self.ExecuteAction(modelToUse, action, dataCollection)
                        .then(result =>
                        {
                            return onSuccess(errorInfo, !MetadataActionHelpers.IsCancelObject(result));
                        });
                }

                if (errorInfo.AlertOnly)
                {
                    return self.$commonDialogsService.showAlertMessage(message, errorInfo.Title, errorInfo.Title, errorInfo.OkLabel)
                        .then(() =>
                        {
                            return onSuccess(errorInfo, true);
                        });
                }

                return self.$commonDialogsService.showConfirmationMessage(
                    message, errorInfo.Title, errorInfo.OkLabel, errorInfo.CancelLabel)
                    .then(
                        () =>
                        {
                            return onSuccess(errorInfo, true);
                        },
                        _x =>
                        {
                            self.$metadataIframeService.MaybeResponseToParentWindow(false);
                            return onSuccess(errorInfo, false);
                        }
                    );
            }
            if (errorInfo.AdditionalInfo)
            {
                title = "Verification Errors";
                const info = JSON.parse(errorInfo.AdditionalInfo);
                if (dataCollection && "Entity" in dataCollection)
                    self.$metadataErrorService.setErrors(info, dataCollection);
                const errors = self.$metadataErrorService.getErrorList(info);
                message = errors.reduce((p, x) => `${p}<li>${x}</li>`, "");
                self.$gridEventService.fireFetchTable();
            }
            if (dataCollection)
                self.ClearVerificationQuestions(dataCollection);

            self.$metadataIframeService.MaybeResponseToParentWindow(false);
            return self.$commonDialogsService.showAlertMessage(message, title)
                .then(
                    _x =>
                    {
                        return Promise.reject(errorInfo);
                    },
                    _x =>
                    {
                        return Promise.reject(errorInfo);
                    }
                );
        };
    }

    private ClearVerificationQuestions(dataCollection: RepositoryDataCollection | any): void
    {
        if (!Utilities.IsNullOrUndefined(dataCollection.ConfirmationQuestions))
            dataCollection.ConfirmationQuestions = undefined;
    }

    isClientSideMetadataAction(action: IMetadataAction): action is IClientSideMetadataAction
    {
        return "$Action" in action;
    }
}
